// src/pages/Appli/AppliAdmin/ListingClients/utils/association.utils.js

import {
  ASSOCIATION_STATUS_INDEX,
  DEFAULT_ASSOCIATION_STATUS,
  getAssociationStatusMeta,
} from "../data/associations.data";

export const EMPTY_ASSOCIATION_FORM = Object.freeze({
  nom_asso: "",
  produit_asso: "",
  statut_asso: DEFAULT_ASSOCIATION_STATUS,
  adresse_asso: "",
  latitude: "",
  longitude: "",
  tel_asso: "",
  mail_asso: "",
  siret_asso: "",
  discipline_asso: "",
  siteweb_asso: "",
  apiUrl_asso: "",
  detail_asso: "",
  logo_asso: "",
  is_favorite: false,
  date_relance_asso: "",
  last_contact_asso: "",
});

export function createEmptyAssociationForm() {
  return { ...EMPTY_ASSOCIATION_FORM };
}

export function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

export function splitDisciplines(value) {
  return String(value || "")
    .split(",")
    .map((discipline) => discipline.trim())
    .filter(Boolean);
}

export function toDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export function associationToFormValues(association) {
  if (!association) {
    return createEmptyAssociationForm();
  }

  return {
    nom_asso: association.nom_asso || "",
    produit_asso: association.produit_asso || "",
    statut_asso:
      association.statut_asso || DEFAULT_ASSOCIATION_STATUS,
    adresse_asso: association.adresse_asso || "",
    latitude: association.latitude ?? "",
    longitude: association.longitude ?? "",
    tel_asso: association.tel_asso || "",
    mail_asso: association.mail_asso || "",
    siret_asso: association.siret_asso || "",
    discipline_asso: association.discipline_asso || "",
    siteweb_asso: association.siteweb_asso || "",
    apiUrl_asso: association.apiUrl_asso || "",
    detail_asso: association.detail_asso || "",
    logo_asso: association.logo_asso || "",
    is_favorite: Boolean(association.is_favorite),
    date_relance_asso: toDateInputValue(
      association.date_relance_asso,
    ),
    last_contact_asso: toDateInputValue(
      association.last_contact_asso,
    ),
  };
}

function nullableString(value) {
  const normalized = String(value || "").trim();
  return normalized || null;
}

function nullableNumber(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).replace(",", ".").trim();
  const number = Number(normalized);

  return Number.isFinite(number) ? number : null;
}

export function buildAssociationPayload(formData) {
  return {
    nom_asso: String(formData.nom_asso || "").trim(),
    produit_asso: String(formData.produit_asso || "").trim(),
    statut_asso:
      formData.statut_asso || DEFAULT_ASSOCIATION_STATUS,

    adresse_asso: nullableString(formData.adresse_asso),
    latitude: nullableNumber(formData.latitude),
    longitude: nullableNumber(formData.longitude),

    tel_asso: nullableString(formData.tel_asso),
    mail_asso: nullableString(formData.mail_asso),
    siret_asso: nullableString(formData.siret_asso),
    discipline_asso: nullableString(formData.discipline_asso),

    siteweb_asso: nullableString(formData.siteweb_asso),
    apiUrl_asso: nullableString(formData.apiUrl_asso),
    logo_asso: nullableString(formData.logo_asso),

    detail_asso: nullableString(formData.detail_asso),

    is_favorite: Boolean(formData.is_favorite),

    date_relance_asso:
      formData.date_relance_asso || null,

    last_contact_asso:
      formData.last_contact_asso || null,
  };
}

function isValidEmail(value) {
  return /^\S+@\S+\.\S+$/.test(value);
}

function isValidHttpUrl(value, { allowRelative = false } = {}) {
  if (!value) return true;

  if (allowRelative && String(value).startsWith("/")) {
    return true;
  }

  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol);
  } catch {
    return false;
  }
}

function parseCoordinate(value) {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  return Number(String(value).replace(",", "."));
}

export function validateAssociationForm(formData) {
  const errors = {};

  if (!String(formData.nom_asso || "").trim()) {
    errors.nom_asso = "Le nom est obligatoire.";
  }

  if (!formData.produit_asso) {
    errors.produit_asso = "Le produit est obligatoire.";
  }

  if (!formData.statut_asso) {
    errors.statut_asso = "Le statut est obligatoire.";
  }

  if (
    formData.mail_asso &&
    !isValidEmail(formData.mail_asso)
  ) {
    errors.mail_asso = "L’adresse email est invalide.";
  }

  if (
    formData.siteweb_asso &&
    !isValidHttpUrl(formData.siteweb_asso)
  ) {
    errors.siteweb_asso =
      "L’adresse doit commencer par http:// ou https://.";
  }

  if (
    formData.apiUrl_asso &&
    !isValidHttpUrl(formData.apiUrl_asso)
  ) {
    errors.apiUrl_asso =
      "L’adresse doit commencer par http:// ou https://.";
  }

  if (
    formData.logo_asso &&
    !isValidHttpUrl(formData.logo_asso, {
      allowRelative: true,
    })
  ) {
    errors.logo_asso =
      "Utilisez une URL complète ou un chemin commençant par /.";
  }

  const latitude = parseCoordinate(formData.latitude);

  if (
    latitude !== null &&
    (!Number.isFinite(latitude) ||
      latitude < -90 ||
      latitude > 90)
  ) {
    errors.latitude =
      "La latitude doit être comprise entre -90 et 90.";
  }

  const longitude = parseCoordinate(formData.longitude);

  if (
    longitude !== null &&
    (!Number.isFinite(longitude) ||
      longitude < -180 ||
      longitude > 180)
  ) {
    errors.longitude =
      "La longitude doit être comprise entre -180 et 180.";
  }

  return errors;
}

export function getAssociationSearchText(association) {
  const prospects = (association.prospects || [])
    .map((prospect) =>
      [
        prospect.nom,
        prospect.prenom,
        prospect.email,
        prospect.telephone,
        prospect.fonction,
      ].join(" "),
    )
    .join(" ");

  return normalizeSearchText(
    [
      association.nom_asso,
      association.produit_asso,
      association.statut_asso,
      association.discipline_asso,
      association.adresse_asso,
      association.mail_asso,
      association.tel_asso,
      association.siret_asso,
      association.detail_asso,
      prospects,
    ].join(" "),
  );
}

export function getLocalToday() {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

export function getReminderState(date) {
  if (!date) {
    return {
      key: "none",
      label: "Aucune relance",
      tone:
        "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300",
    };
  }

  const dateValue = toDateInputValue(date);
  const today = getLocalToday();

  if (dateValue < today) {
    return {
      key: "overdue",
      label: "En retard",
      tone:
        "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300",
    };
  }

  if (dateValue === today) {
    return {
      key: "today",
      label: "Aujourd’hui",
      tone:
        "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    };
  }

  return {
    key: "upcoming",
    label: "À venir",
    tone:
      "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300",
  };
}

export function isReminderDue(date) {
  const state = getReminderState(date);
  return ["overdue", "today"].includes(state.key);
}

export function formatAssociationDate(value) {
  const dateValue = toDateInputValue(value);

  if (!dateValue) return "—";

  const [year, month, day] = dateValue
    .split("-")
    .map(Number);

  const date = new Date(year, month - 1, day);

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function compareAssociationStatuses(first, second) {
  const firstIndex =
    ASSOCIATION_STATUS_INDEX[first.statut_asso] ?? 999;

  const secondIndex =
    ASSOCIATION_STATUS_INDEX[second.statut_asso] ?? 999;

  if (firstIndex !== secondIndex) {
    return firstIndex - secondIndex;
  }

  return String(first.nom_asso || "").localeCompare(
    String(second.nom_asso || ""),
    "fr",
  );
}

export function getAssociationGroup(association) {
  return getAssociationStatusMeta(
    association.statut_asso,
  ).group;
}

export function getAssociationApiError(
  error,
  fallbackMessage = "Une erreur est survenue.",
) {
  const data = error?.response?.data;

  if (typeof data === "string") {
    return data;
  }

  return (
    data?.message ||
    data?.error ||
    error?.message ||
    fallbackMessage
  );
}